import React, { useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { subirComprobante, agregarComprobantesInstruccion, eliminarComprobanteInstruccion, reemplazarComprobanteInstruccion } from '../../services/data';
import { comprimir } from '../finanzas/Comprobante';
import { useProtectedFileUrl } from '../../hooks/useProtectedFileUrl';
import { formatDateTime } from '../../utils/dates';
import '../finanzas/ui.css';

const MAX_BYTES = 10 * 1024 * 1024;
const TIPOS_OK = /^image\/|^application\/pdf$/;
const hora = formatDateTime;
const nombreSeguro = (f) => (f.name || 'comprobante').replace(/[^\w.-]+/g, '_');

function ArchivoVinculado({ archivo, mini = false }) {
  const protegido = useProtectedFileUrl(archivo.url);
  if (protegido.error) return <span>Comprobante no disponible</span>;
  if (!protegido.url) return <span>Cargando…</span>;
  return <a href={protegido.url} target="_blank" rel="noreferrer" title="Ver imagen completa" className={mini ? 'liq-comp-mini' : undefined}>
    {mini && /^image\//.test(archivo.tipoArchivo) ? <img src={protegido.url} alt={archivo.nombre || 'Comprobante'} loading="lazy" /> : mini ? <span aria-hidden="true">📄</span> : archivo.nombre || 'Comprobante'}
  </a>;
}

/** Valida, reduce y sube un archivo; devuelve su URL. */
async function subir(f) {
  if (!TIPOS_OK.test(f.type)) throw new Error(`${f.name || 'Archivo'}: usa una foto o un PDF.`);
  const listo = await comprimir(f);
  if (listo.size > MAX_BYTES) throw new Error(`${f.name || 'Archivo'}: supera 10 MB.`);
  return subirComprobante(listo, nombreSeguro(listo), listo.type);
}

/**
 * Comprobantes de una instrucción (evidencia; no cambian el estado ni el dinero).
 * 📷 toma una foto · 📎 adjunta una o varias imágenes · miniatura → imagen completa · Reemplazar · Quitar (queda en el historial).
 * `respalda`: 'instruccion' (lo indicado) o 'resultado' (destino final tras un cambio de destino).
 */
export default function ComprobantesInstruccion({ instruccion: i, respalda = 'instruccion', puedeAgregar = true, onChanged }) {
  const camara = useRef(null);
  const galeria = useRef(null);
  const reemplazo = useRef(null);
  const [reemplazando, setReemplazando] = useState(null);
  const [observacion, setObservacion] = useState('');
  const [busy, setBusy] = useState(false);
  const lista = (i.comprobantes || []).filter((x) => x.respalda === respalda);
  const quitados = (i.comprobantesEliminados || []).filter((x) => x.respalda === respalda);

  const accion = async (fn) => {
    setBusy(true);
    try { await fn(); await onChanged?.(); }
    catch (e) { Swal.fire('Comprobantes', e.message || 'No se pudo completar.', 'error'); }
    finally { setBusy(false); }
  };
  const elegir = (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = ''; // permite volver a elegir los mismos archivos
    if (!files.length) return;
    accion(async () => {
      const urls = [];
      for (const f of files) urls.push(await subir(f)); // uno por uno: fotos grandes del celular
      await agregarComprobantesInstruccion(i.id, { archivos: urls.map((url) => ({ url })), observacion: observacion.trim(), respalda });
      setObservacion('');
    });
  };
  const elegirReemplazo = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    const x = reemplazando;
    setReemplazando(null);
    if (!f || !x) return;
    accion(async () => reemplazarComprobanteInstruccion(x.id, { url: await subir(f) }));
  };
  const quitar = async (x) => {
    const { value: motivo } = await Swal.fire({ title: 'Quitar comprobante', input: 'text', inputLabel: 'Motivo',
      text: `${x.nombre || 'Comprobante'}. Quedará en el historial como quitado.`, showCancelButton: true, confirmButtonText: 'Quitar',
      inputValidator: (v) => (!v?.trim() ? 'Indica el motivo' : undefined) });
    if (motivo) accion(() => eliminarComprobanteInstruccion(x.id, motivo.trim()));
  };

  return (
    <div className="liq-comps">
      <input ref={camara} type="file" accept="image/*" capture="environment" hidden onChange={elegir} />
      <input ref={galeria} type="file" accept="image/*,application/pdf" multiple hidden onChange={elegir} />
      <input ref={reemplazo} type="file" accept="image/*,application/pdf" hidden onChange={elegirReemplazo} />
      {lista.length ? (
        <ul className="liq-comps-lista">
          {lista.map((x) => (
            <li key={x.id} className="liq-comp">
              <ArchivoVinculado archivo={x} mini />
              <div className="liq-comp-info">
                <b title={x.nombre}>{x.nombre || 'Comprobante'}</b>
                <small>{x.cargadoPor} · {hora(x.cargadoAt)}</small>
                {x.observacion && <small>{x.observacion}</small>}
                {puedeAgregar && <span className="liq-comp-acc">
                  <button type="button" className="fx-link" disabled={busy} onClick={() => { setReemplazando(x); reemplazo.current?.click(); }}>Reemplazar</button>
                  <button type="button" className="fx-link" style={{ color: 'var(--danger)' }} disabled={busy} onClick={() => quitar(x)}>Quitar</button>
                </span>}
              </div>
            </li>))}
        </ul>
      ) : <small className="fx-empty liq-comps-vacio">Sin comprobantes.</small>}
      {puedeAgregar && <div className="liq-comps-agregar">
        <input className="liq-comps-obs" value={observacion} onChange={(e) => setObservacion(e.target.value)} maxLength={300} placeholder="Observación (opcional)" aria-label="Observación del comprobante" />
        <span className="fx-adjunto-iconos">
          <button type="button" className="fx-adjunto-icono-btn" disabled={busy} onClick={() => camara.current?.click()} aria-label="Tomar foto">
            <span className="fx-adjunto-ico" aria-hidden="true">📷</span><small>Tomar foto</small></button>
          <button type="button" className="fx-adjunto-icono-btn" disabled={busy} onClick={() => galeria.current?.click()} aria-label="Adjuntar una o varias fotos">
            <span className="fx-adjunto-ico" aria-hidden="true">📎</span><small>Adjuntar foto</small></button>
        </span>
        {busy && <small className="fx-empty">Subiendo…</small>}
      </div>}
      {quitados.length > 0 && <details className="liq-comps-hist">
        <summary>Historial: {quitados.length} comprobante(s) quitado(s) o reemplazado(s)</summary>
        <ul>{quitados.map((x) => <li key={x.id}><ArchivoVinculado archivo={x} /> · cargado por {x.cargadoPor} {hora(x.cargadoAt)}
          · {x.eliminadoMotivo === 'Reemplazado' ? 'reemplazado' : `quitado ("${x.eliminadoMotivo}")`} por {x.eliminadoPor} {hora(x.eliminadoAt)}</li>)}</ul>
      </details>}
    </div>
  );
}
